<script lang="ts">
  import { withUniqueKeys } from '../lib/unique-keys';

  /** One tab entry rendered by {@link TabBar}. */
  export interface Tab {
    key: string;
    label: string;
  }

  interface Properties {
    tabs: Tab[];
    active: string;
    onSelect: (key: string) => void;
  }

  const { tabs, active, onSelect }: Properties = $props();

  /** Tabs paired with list-unique keys; `tabs` is a prop, so its keys are the caller's. */
  const keyedTabs = $derived(withUniqueKeys(tabs, (entry) => entry.key));
</script>

<!-- Horizontal tab strip. Emits `onSelect(key)` when the user clicks a tab;
     the caller drives the `active` prop from its own state / URL sync. -->
<div class="tab-bar" role="tablist">
  {#each keyedTabs as entry (entry.key)}
    {@const item = entry.item}
    <button
      type="button"
      class="tab-btn"
      class:active={active === item.key}
      role="tab"
      aria-selected={active === item.key}
      onclick={() => {
        onSelect(item.key);
      }}>{item.label}</button
    >
  {/each}
</div>

<style>
  .tab-bar {
    display: flex;
    gap: var(--space-xs);
    border-bottom: 1px solid var(--color-border);
    margin-bottom: var(--space-md);

    /* Sticky (not fixed) — flows with content at the top of the panel and
       pins to the panel edge only when the user scrolls past it. Without
       this, an expanded journey detail or a long results list pushes the
       tab bar off-screen and Route/Departures become unclickable. */
    position: sticky;
    top: 0;
    background: var(--color-bg);
    z-index: 10;
    padding-top: var(--space-xs);
  }

  .tab-btn {
    background: none;
    border: none;
    color: var(--color-fg-muted);
    padding: var(--space-sm) var(--space-md);
    cursor: pointer;
    font-size: var(--font-size-md);
    border-bottom: 2px solid transparent;
  }

  .tab-btn:hover {
    color: var(--color-fg);
  }

  .tab-btn.active {
    color: var(--color-primary);
    border-bottom-color: var(--color-primary);
  }
</style>
